import { useEffect, useRef, useState, type ChangeEvent, type CSSProperties } from 'react';
import { api } from '../api';
import type { Tarif } from '../billtoro/types';
import { fehlendeFunktion, planName, tarifFehlerFuer, upgradeAnbieten } from '../billtoro/upgrade';
import { PdfPages } from '../components/Pdf';
import { fmtDate } from '../lib/format';
import { useToast } from '../lib/toast';
import { fehlerText, NurLesen, OHNE_RECHT_EINSTELLUNGEN, TarifMarke } from './tarifHinweis';
import './vorlagen.css';

// Vorlage der PDFs und eigenes Briefpapier (Einstellungen, server vorlagen.ts und briefpapier.ts): vier Vorlagen zum
// Anklicken mit kleinem Bild, daneben das echte Beispiel-PDF mit der gewählten Vorlage und Farbe (noch nicht
// gespeichert). Darunter das Briefpapier: hochladen, ansehen, entfernen, Hinweise, wo auf dem Blatt schon etwas steht.
// Billtoro ohne „logo“ im Tarif: nur die Bilder (das Beispiel-PDF wäre Klassisch), speichern öffnet „Mehr mit Billtoro“.

export type VorlageId = 'klassisch' | 'modern' | 'kompakt' | 'elegant';
type Zone = 'kopf' | 'fenster' | 'angaben' | 'fuss' | 'folge';
export interface BriefpapierInfo {
  name: string;
  bytes: number;
  seiten: 1 | 2;
  hinweise: Zone[];
  updatedAt: string;
}
export interface VorlageInfo {
  vorlage: VorlageId;
  ohneKopf: boolean;
  briefpapier: BriefpapierInfo | null;
}

const VORLAGEN: { id: VorlageId; name: string; text: string }[] = [
  { id: 'klassisch', name: 'Klassisch', text: 'Schlicht, wie bisher.' },
  { id: 'modern', name: 'Modern', text: 'Farbiges Band oben, Logo links.' },
  { id: 'kompakt', name: 'Kompakt', text: 'Kleinere Schrift, mehr Positionen auf einer Seite.' },
  { id: 'elegant', name: 'Elegant', text: 'Schrift mit Serifen, Name in der Mitte.' },
];
export const vorlageName = (v: VorlageId) => VORLAGEN.find((x) => x.id === v)?.name ?? v;

const HINWEIS: Record<Zone, string> = {
  kopf: 'Oben auf dem Briefpapier steht schon etwas. Tipp: Firmenname und Logo oben weglassen.',
  fenster: 'Im Feld für die Anschrift steht schon etwas. Dort steht später die Anschrift Ihres Kunden.',
  angaben: 'Rechts neben der Anschrift steht schon etwas. Dort stehen Nummer und Datum des Belegs.',
  fuss: 'Unten auf der Seite steht schon etwas. Dort stehen Ihre Pflichtangaben und Ihre Bankverbindung. Bitte prüfen Sie die Vorschau.',
  folge: 'Auf der Folgeseite steht etwas in der Mitte. Dort stehen die weiteren Positionen.',
};

// Standard: die bisherige Schriftfarbe der PDFs
const STANDARD = '#0f1b2d';

export function Vorlagen({
  firmId,
  farbe,
  wahl,
  onWahl,
  gespeichert,
  onSaved,
  tarif,
  lesen = false,
  postAn = false,
}: {
  firmId: string;
  // gewählte Farbe aus „Logo und Farbe“ (auch noch nicht gespeichert), null: Standard
  farbe: string | null;
  // gewählte Vorlage (auch noch nicht gespeichert)
  wahl: VorlageId;
  onWahl(v: VorlageId): void;
  gespeichert: VorlageInfo;
  onSaved(v: VorlageInfo): void;
  tarif?: Tarif | null;
  lesen?: boolean;
  // Briefversand per Post eingerichtet
  postAn?: boolean;
}) {
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bpError, setBpError] = useState<string | null>(null);
  const [viele, setViele] = useState(false);
  // der Haken folgt dem Klick gleich, bei einem Fehler springt er zurück
  const [ohneKopf, setOhneKopf] = useState(gespeichert.ohneKopf);
  const logoFehlt = fehlendeFunktion(tarif, 'logo');
  const geaendert = wahl !== gespeichert.vorlage;
  const bp = gespeichert.briefpapier;
  // ohne Tarif zeigte das Beispiel-PDF nur Klassisch: dann nur die Bilder, etwas größer
  const vorschau = !logoFehlt;

  // Vorschau erst, wenn sich die Wahl eine halbe Sekunde nicht mehr ändert (die Farbwahl meldet laufend, der Server
  // erlaubt 30 Beispiele in der Minute)
  const neu = `/api/rechnungstool/${firmId}/einstellungen/beispiel?farbe=${encodeURIComponent(farbe ?? '')}&vorlage=${wahl}${viele ? '&seiten=2' : ''}`;
  const [url, setUrl] = useState(neu);
  useEffect(() => {
    const t = setTimeout(() => setUrl(neu), 500);
    return () => clearTimeout(t);
  }, [neu]);

  async function speichern() {
    if (wahl !== 'klassisch' && upgradeAnbieten(tarif, 'logo')) return;
    setError(null);
    setBusy(true);
    try {
      const v = await api.put<VorlageInfo>(`/api/rechnungstool/${firmId}/einstellungen/vorlage`, { vorlage: wahl });
      onSaved(v);
      onWahl(v.vorlage);
      toast('Vorlage gespeichert');
    } catch (err) {
      setError(fehlerText(err));
    } finally {
      setBusy(false);
    }
  }

  async function kopfUmschalten(an: boolean) {
    setBpError(null);
    setBusy(true);
    setOhneKopf(an);
    try {
      const v = await api.put<VorlageInfo>(`/api/rechnungstool/${firmId}/einstellungen/vorlage`, { vorlage: gespeichert.vorlage, ohneKopf: an });
      setOhneKopf(v.ohneKopf);
      onSaved(v);
      toast('Gespeichert');
    } catch (err) {
      setOhneKopf(!an);
      setBpError(fehlerText(err));
    } finally {
      setBusy(false);
    }
  }

  async function hochladen(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setBpError(null);
    setBusy(true);
    const fd = new FormData();
    fd.append('file', file, file.name);
    try {
      const b = await api.put<BriefpapierInfo>(`/api/rechnungstool/${firmId}/briefpapier`, fd);
      onSaved({ ...gespeichert, briefpapier: b });
      toast('Briefpapier gespeichert');
    } catch (err) {
      setBpError(fehlerText(err));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  }

  async function entfernen() {
    setBpError(null);
    setBusy(true);
    try {
      await api.del(`/api/rechnungstool/${firmId}/briefpapier`);
      onSaved({ ...gespeichert, briefpapier: null });
      toast('Briefpapier entfernt');
    } catch (err) {
      setBpError(fehlerText(err));
    } finally {
      setBusy(false);
    }
  }

  const bild = (seite: '1' | 'folge') => `/api/rechnungstool/${firmId}/briefpapier/${seite}?v=${encodeURIComponent(bp?.updatedAt ?? '')}`;
  const hinweise = (bp?.hinweise ?? []).filter((z) => z !== 'kopf' || !ohneKopf);

  return (
    <section className="rt-card rt-vl" aria-labelledby="rt-vl-h">
      <h2 id="rt-vl-h">
        Vorlage
        <TarifMarke tarif={tarif} fehlt={logoFehlt} />
      </h2>
      <p className="rt-hint">So sehen Rechnungen, Angebote, Mahnungen und alle anderen Belege aus.</p>
      {tarif && logoFehlt && <p className="rt-hint">{tarifFehlerFuer(tarif, 'logo')?.error} Hier sehen Sie schon, wie es aussieht.</p>}
      {tarif && logoFehlt && gespeichert.vorlage !== 'klassisch' && (
        <p className="rt-hint">
          Ihre Vorlage „{vorlageName(gespeichert.vorlage)}“ ruht. Neue Belege sehen im Tarif {planName(tarif, tarif.effektiv)} so aus wie „Klassisch“.
        </p>
      )}
      <div className={`rt-vl-cols${vorschau ? '' : ' gross'}`}>
        <div>
          <NurLesen an={lesen} hinweis={OHNE_RECHT_EINSTELLUNGEN}>
            <div className="rt-vl-wahl" role="radiogroup" aria-labelledby="rt-vl-h">
              {VORLAGEN.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  role="radio"
                  aria-checked={wahl === v.id}
                  className={`rt-vl-karte${wahl === v.id ? ' on' : ''}`}
                  onClick={() => onWahl(v.id)}
                >
                  <VorlageBild id={v.id} farbe={farbe ?? STANDARD} />
                  <b>{v.name}</b>
                  <span>{v.text}</span>
                </button>
              ))}
            </div>
            {error && <div className="notice bad">{error}</div>}
            {geaendert && (
              <div className="rt-actions">
                <button type="button" className="btn" onClick={() => onWahl(gespeichert.vorlage)} disabled={busy}>
                  Zurücksetzen
                </button>
                <button
                  type="button"
                  className={logoFehlt && wahl !== 'klassisch' ? 'btn primary rt-gesperrt' : 'btn primary'}
                  onClick={() => void speichern()}
                  disabled={busy || (logoFehlt === 'bald' && wahl !== 'klassisch')}
                >
                  Vorlage speichern
                  {wahl !== 'klassisch' && <TarifMarke tarif={tarif} fehlt={logoFehlt} />}
                </button>
              </div>
            )}
          </NurLesen>
        </div>
        {vorschau && (
          <div className="rt-vl-vorschau">
            <div className="rt-vl-vk">
              <span>Vorschau mit Musterdaten</span>
              <button type="button" className="linkbtn" onClick={() => setViele((x) => !x)}>
                {viele ? 'Nur eine Seite' : 'Mit vielen Positionen'}
              </button>
            </div>
            <div className="rt-vl-pdf">
              <PdfPages url={url} title="Vorschau" fallback={<p className="rt-hint">Die Vorschau lässt sich gerade nicht laden.</p>} />
            </div>
          </div>
        )}
      </div>

      <h3>Eigenes Briefpapier</h3>
      {/* nur zum Ansehen wie oben, der Hinweis steht einmal je Karte (oben bei der Vorlage) */}
      <NurLesen an={lesen} ohneHinweis>
        {bp ? (
          <div className="rt-vl-bp">
            <figure>
              <img src={bild('1')} alt="Erste Seite des Briefpapiers" />
              <figcaption>Erste Seite</figcaption>
            </figure>
            <figure>
              {bp.seiten === 2 ? <img src={bild('folge')} alt="Folgeseite des Briefpapiers" /> : <span className="rt-vl-leer" />}
              <figcaption>{bp.seiten === 2 ? 'Folgeseiten' : 'Folgeseiten: ohne Briefpapier'}</figcaption>
            </figure>
            <p className="rt-hint rt-vl-datei">
              {bp.name}
              <br />
              hochgeladen am {fmtDate(bp.updatedAt)}
            </p>
          </div>
        ) : (
          <p className="rt-hint">
            Laden Sie Ihr Briefpapier als PDF hoch. Die erste Seite liegt unter der ersten Seite jedes Belegs. Hat die PDF eine zweite Seite,
            liegt sie unter allen weiteren Seiten.
          </p>
        )}
        <div className="rt-vl-akt">
          <button
            type="button"
            className={logoFehlt ? 'btn rt-gesperrt' : 'btn'}
            onClick={() => (logoFehlt ? upgradeAnbieten(tarif, 'logo') : input.current?.click())}
            disabled={busy || logoFehlt === 'bald'}
          >
            {bp ? 'Anderes Briefpapier' : 'Briefpapier hochladen'}
            <TarifMarke tarif={tarif} fehlt={logoFehlt} />
          </button>
          {bp && (
            <button type="button" className="linkbtn rt-danger" onClick={() => void entfernen()} disabled={busy}>
              Briefpapier entfernen
            </button>
          )}
          <input ref={input} type="file" accept="application/pdf" hidden onChange={(e) => void hochladen(e)} aria-label="Briefpapier-Datei" />
        </div>
        <p className="rt-hint">
          PDF im Format A4 hochkant, höchstens 5 MB, 1 oder 2 Seiten. Unten auf der Seite stehen immer Ihre Pflichtangaben und Ihre
          Bankverbindung. Lassen Sie dort etwa 3,5 cm frei.
        </p>
        {bp && (
          <label className="check">
            <input type="checkbox" checked={ohneKopf} disabled={busy} onChange={(e) => void kopfUmschalten(e.target.checked)} />
            <span>Firmenname und Logo oben weglassen (stehen schon auf dem Briefpapier)</span>
          </label>
        )}
        {hinweise.map((z) => (
          <div key={z} className="notice warn">
            {HINWEIS[z]}
          </div>
        ))}
        {postAn && <p className="rt-hint">Briefe per Post gehen ohne Briefpapier hinaus, damit die Anschrift sicher ins Fenster passt.</p>}
        {bpError && <div className="notice bad">{bpError}</div>}
      </NurLesen>
    </section>
  );
}

// Kleines Bild der Vorlage (nur CSS, in der gewählten Farbe): wo Name, Logo, Linien und Positionen stehen
function VorlageBild({ id, farbe }: { id: VorlageId; farbe: string }) {
  return (
    <span className={`rt-vl-bild ${id}`} style={{ '--f': farbe } as CSSProperties} aria-hidden="true">
      {id === 'modern' ? (
        <span className="k">
          <i className="q" />
          <i className="n" />
        </span>
      ) : id === 'elegant' ? (
        <span className="k">
          <i className="q" />
          <i className="n" />
          <i className="d" />
        </span>
      ) : (
        <span className="k">
          <i className="n" />
          <i className="q" />
        </span>
      )}
      <b className="t">Rechnung</b>
      {Array.from({ length: id === 'kompakt' ? 8 : 4 }, (_, i) => (
        <i key={i} className="z" />
      ))}
      <i className="s" />
    </span>
  );
}
